import { ArrowRight, Camera, Check, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck, Sparkles, WandSparkles } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input, Notice } from '../components/UI'
import { useMarlovaStore } from '../store'
import { api, ApiError } from '../api/client'

export function WelcomePage() {
  const navigate = useNavigate()
  const loadDemo = useMarlovaStore((state) => state.loadDemo)
  const authenticated = useMarlovaStore((state) => state.authenticated)
  const account = useMarlovaStore((state) => state.account)
  if (authenticated && account) return <Navigate to={account.onboardingStep === 'completed' ? '/today' : `/onboarding/${account.onboardingStep}`} replace />

  return (
    <div className="welcome">
      <header className="welcome__nav">
        <img src="/brand/marlova-horizontal-compact.svg" alt="Marlova" />
        <div><Link to="/login" className="welcome__login">Log in</Link><Button size="sm" onClick={() => navigate('/register')}>Get started</Button></div>
      </header>
      <main className="welcome__main">
        <section className="welcome__copy">
          <div className="welcome__badge"><Sparkles size={16} /> Nutrition logging, made lighter</div>
          <h1>Know what’s on your plate.<br/><em>Keep moving forward.</em></h1>
          <p>Take a meal photo, add what you know, and get a practical nutrition estimate you can review and correct.</p>
          <div className="welcome__actions">
            <Button size="lg" onClick={() => navigate('/register')}>Get started <ArrowRight size={19} /></Button>
            <Button size="lg" variant="secondary" onClick={() => { loadDemo(); navigate('/today') }}>Explore the demo</Button>
          </div>
          <div className="welcome__reassurance"><ShieldCheck size={18} /><span>Estimates, not medical advice. Your context always takes priority.</span></div>
        </section>
        <section className="welcome__visual" aria-label="Example nutrition analysis">
          <div className="hero-orbit hero-orbit--one" /><div className="hero-orbit hero-orbit--two" />
          <div className="hero-phone">
            <div className="hero-phone__top"><span>Today</span><span>•••</span></div>
            <div className="hero-photo">
              <div className="plate"><i /><i /><i /><i /></div>
              <span className="hero-photo__tag hero-photo__tag--one">Salmon</span>
              <span className="hero-photo__tag hero-photo__tag--two">Rice</span>
              <span className="hero-photo__tag hero-photo__tag--three">Greens</span>
            </div>
            <div className="hero-result">
              <div><span>Estimated meal</span><strong>Salmon rice bowl</strong></div>
              <span className="confidence-pill">Moderate</span>
              <strong className="hero-result__kcal">635 <small>kcal</small></strong>
              <div className="hero-macros"><span><b>39 g</b>Protein</span><span><b>74 g</b>Carbs</span><span><b>21 g</b>Fat</span></div>
            </div>
          </div>
          <div className="floating-card floating-card--scan"><WandSparkles /><span><strong>Photo analyzed</strong>Ready to review</span><Check /></div>
          <div className="floating-card floating-card--speed"><b>~40 sec</b><span>to a useful estimate</span></div>
        </section>
      </main>
      <section className="welcome__steps">
        <div><span><Camera /></span><strong>1. Take a photo</strong><p>Frame the full meal in clear light.</p></div>
        <div><span><WandSparkles /></span><strong>2. Review the estimate</strong><p>See portions, nutrition, and uncertainty.</p></div>
        <div><span><Check /></span><strong>3. Make it yours</strong><p>Correct details, then save your meal.</p></div>
      </section>
      <footer className="welcome__footer"><span>© 2026 Marlova</span><nav><a href="#privacy">Privacy summary</a><a href="#terms">Terms</a><a href="mailto:support@marlova.app">Support</a></nav></footer>
    </div>
  )
}

type AuthMode = 'login' | 'register' | 'forgot' | 'reset' | 'verify'

const authCopy: Record<AuthMode, { eyebrow: string; title: string; description: string }> = {
  login: { eyebrow: 'Welcome back', title: 'Log in to Marlova', description: 'Continue where you left off.' },
  register: { eyebrow: 'Create your account', title: 'A clearer way to log meals', description: 'Start with a photo. Keep the final say.' },
  forgot: { eyebrow: 'Password help', title: 'Reset your password', description: 'We’ll send a secure reset link to your email.' },
  reset: { eyebrow: 'Choose a password', title: 'Set a new password', description: 'Use at least 10 characters.' },
  verify: { eyebrow: 'One more step', title: 'Check your inbox', description: 'Verify your email before analyzing meal images.' },
}

export function AuthPage({ mode }: { mode: AuthMode }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const register = useMarlovaStore((state) => state.register)
  const login = useMarlovaStore((state) => state.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [adult, setAdult] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const copy = authCopy[mode]

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (mode === 'forgot') {
      setBusy(true)
      try { await api.forgotPassword(email); setSubmitted(true) } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Password reset is temporarily unavailable.') } finally { setBusy(false) }
      return
    }
    if (mode === 'reset') {
      const resetEmail = searchParams.get('email') ?? ''
      const token = searchParams.get('token') ?? ''
      if (password.length < 10) return setError('Use at least 10 characters for your password.')
      if (password !== confirmPassword) return setError('The passwords do not match.')
      if (!resetEmail || !token) return setError('This password-reset link is incomplete or expired. Request a new one.')
      setBusy(true)
      try { await api.resetPassword(resetEmail, token, password, confirmPassword); setSubmitted(true) } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Your password could not be reset.') } finally { setBusy(false) }
      return
    }
    if (mode === 'register') {
      if (password.length < 10) return setError('Use at least 10 characters for your password.')
      if (password !== confirmPassword) return setError('The passwords do not match.')
      if (!accepted || !adult) return setError('Confirm the terms, privacy notice, and 18+ requirement.')
      setBusy(true)
      try { await register(email, password); navigate('/onboarding/profile') } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Your account could not be created.') } finally { setBusy(false) }
      return
    }
    setBusy(true)
    try {
      await login(email, password)
      const account = useMarlovaStore.getState().account
      navigate(account?.onboardingStep === 'completed' ? '/today' : `/onboarding/${account?.onboardingStep ?? 'profile'}`)
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'Marlova could not log you in.') } finally { setBusy(false) }
  }

  if (mode === 'verify') {
    return <AuthLayout copy={copy}><div className="verification-art"><span><Mail size={38} /></span></div><Notice title="Verification link sent">Open the link in your email before analyzing a meal. You can finish setting up your profile now.</Notice><Button size="lg" onClick={() => navigate('/onboarding/profile')}>Continue to profile <ArrowRight size={18} /></Button></AuthLayout>
  }

  return (
    <AuthLayout copy={copy}>
      {submitted ? (
        <div className="auth-success"><span><Check size={32} /></span><h2>{mode === 'forgot' ? 'Check your email' : 'Password updated'}</h2><p>{mode === 'forgot' ? `If an account exists for ${email}, a reset link is on its way.` : 'You can now log in with your new password.'}</p><Button onClick={() => navigate('/login')}>Back to login</Button></div>
      ) : (
        <form className="auth-form" onSubmit={submit} noValidate>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {mode !== 'reset' ? <Field label="Email address" required><div className="input-with-icon"><Mail /><Input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required /></div></Field> : null}
          {mode !== 'forgot' ? <Field label={mode === 'reset' ? 'New password' : 'Password'} hint={mode === 'register' || mode === 'reset' ? 'At least 10 characters' : undefined} required><div className="input-with-icon"><LockKeyhole /><Input type={showPassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={(event) => setPassword(event.target.value)} required /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff /> : <Eye />}</button></div></Field> : null}
          {mode === 'register' || mode === 'reset' ? <Field label="Confirm password" required><Input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></Field> : null}
          {mode === 'login' ? <div className="auth-form__row"><label><input type="checkbox" /> Remember me</label><Link to="/forgot-password">Forgot password?</Link></div> : null}
          {mode === 'register' ? <div className="auth-consents"><label><input type="checkbox" checked={adult} onChange={(event) => setAdult(event.target.checked)} /><span>I confirm I am at least 18 years old.</span></label><label><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span>I agree to the <a href="#terms">Terms</a> and acknowledge the <a href="#privacy">Privacy Notice</a>.</span></label></div> : null}
          <Button size="lg" type="submit" loading={busy} disabled={busy}>{mode === 'login' ? 'Log in' : mode === 'register' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Update password'} <ArrowRight size={18} /></Button>
          <p className="auth-switch">{mode === 'login' ? <>New to Marlova? <Link to="/register">Create an account</Link></> : mode === 'register' ? <>Already have an account? <Link to="/login">Log in</Link></> : <Link to="/login">Back to login</Link>}</p>
        </form>
      )}
    </AuthLayout>
  )
}

function AuthLayout({ copy, children }: { copy: { eyebrow: string; title: string; description: string }; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <Link to="/welcome" className="auth-page__brand"><img src="/brand/marlova-horizontal-compact.svg" alt="Marlova" /></Link>
      <main className="auth-card"><p className="eyebrow">{copy.eyebrow}</p><h1>{copy.title}</h1><p className="auth-card__description">{copy.description}</p>{children}<small>Nutrition values are estimates. Marlova does not provide medical advice.</small></main>
      <div className="auth-page__pattern" aria-hidden="true" />
    </div>
  )
}
